import D from "@/modules/dashboard/Dashboard.module.css";
import C from "@/modules/dashboard/WelcomeBlock.module.css";

import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { ApiDocLink } from "@/modules/access/token/ApiDocLink.tsx";
import { accessCreateLink } from "@/modules/api/resourceCreateLinks.ts";
import { getResourceDescription, resourceTypeEnabledAtom } from "@/modules/api/resourceTypes.tsx";
import { isMobileDevice } from "@/modules/main/mobile/isMobile.ts";
import type { DesktopBannerProps } from "@/modules/theme/types.ts";
import { useThemeProps } from "@/modules/theme/useTheme.ts";
import { cn } from "@/utils/classNames.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { Link as RouterLink, useNavigate } from "@tanstack/react-router";
import { discardPromise } from "@warrenio/utils/promise/discardPromise";
import { useAtomValue } from "jotai";

const firewallBanner: DesktopBannerProps = {
    title: <Trans>Firewall now available</Trans>,
    subtitle: getResourceDescription("firewall"),
    icon: "i-lucide:shield-ban",
    url: "/network/create/firewall",
};

function DesktopBanner() {
    const { desktopBanner: themeDesktopBanner } = useThemeProps();
    const navigate = useNavigate();

    const firewallEnabled = useAtomValue(resourceTypeEnabledAtom("firewall"));

    const desktopBanner = themeDesktopBanner ?? (firewallEnabled ? firewallBanner : undefined);

    if (!desktopBanner) {
        return null;
    }

    const onClick = () => {
        discardPromise(navigate({ to: desktopBanner.url }));
    };

    return (
        <div className={cn(D.Block, "text-center", C.Banner)} onClick={onClick}>
            <div className={C.BannerContent}>
                <MaskIcon className={cn(desktopBanner.icon, "size-1.75rem color-primary")} />
                <h2 className="font-size-title">{desktopBanner.title}</h2>
                <div className="font-size-small text-primary">{desktopBanner.subtitle}</div>
            </div>

            <div className={C.BannerBg} style={isMobileDevice ? undefined : desktopBanner.style} />
        </div>
    );
}

export function WelcomeBlock() {
    const { t } = useLingui();
    const {
        welcomeText = t`We've assembled some links to get you started. Develop, test code, and deploy your applications in no time, from prototyping to large-scale development.`,
    } = useThemeProps();

    return (
        <div className={C.WelcomeBlock}>
            <div className={cn(D.Block, C.PaddedBlock, "flex", C.Welcome)}>
                <div className="VStack gap-2">
                    <h1 className="font-size-heading">
                        <Trans>Welcome</Trans>
                    </h1>
                    <p className="color-muted">{welcomeText}</p>
                </div>

                <div className={C.Links}>
                    <h2 className="font-size-default">
                        <Trans>API</Trans>
                    </h2>
                    <ApiDocLink>
                        <MaskIcon className="jp-icon-text-editor size-0.875rem" /> <Trans>API documentation</Trans>
                    </ApiDocLink>
                    <RouterLink {...accessCreateLink} className="react-aria-Link">
                        <MaskIcon className="jp-swap-icon size-0.875rem" /> <Trans>Manage API keys</Trans>
                    </RouterLink>
                </div>
            </div>

            <DesktopBanner />
        </div>
    );
}
